/**
 * Mobile Links create form (POS-I277, discounts POS-I275): form state, validation, payload and the
 * "This link can / cannot / limits" summary. Pure on purpose: wording logic lives here, covered by link-form.spec.ts,
 * and the components only render it.
 */

export type SectionKey = 'basics' | 'activities' | 'discounts' | 'limits' | 'review';
export const SECTIONS: SectionKey[] = ['basics', 'activities', 'discounts', 'limits', 'review'];

export const DISCOUNT_SCOPE = 'pos:discount';
export const SELL_SCOPE = 'pos:create';
export const EXPIRY_CHOICES = ['1', '8', '24', '72', '168', '720'] as const;
export const MAX_EXPIRY_HOURS = 720;
export const DEFAULT_EXPIRY = '24';

export interface LinkForm {
  name: string;
  /** One of EXPIRY_CHOICES or 'other' (then expiryOther holds the number of hours). */
  expiry: string;
  expiryOther: string;
  locationId: string;
  maxUses: string;
  scopes: string[];
  maxSaleAmount: string;
  maxSalesPerHour: string;
  dailyTotalCap: string;
  allowedFrom: string;
  allowedTo: string;
  allowedCidrs: string;
  bindDevice: boolean;
  maxDiscountPercent: string;
  maxDiscountAmount: string;
}

export interface CatalogEntry {
  key: string;
  label: string;
  grantable: boolean;
}

export interface FormProblem {
  /** DOM id of the field to focus. */
  field: string;
  section: SectionKey;
  /** i18n key (namespace mobileLinks). */
  key: string;
  params?: Record<string, string | number>;
}

export interface ValidateCtx {
  /** The shop's manager-PIN discount threshold when known (from the server's refusal). */
  pinThreshold?: number | null;
}

export const emptyForm = (): LinkForm => ({
  name: '',
  expiry: DEFAULT_EXPIRY,
  expiryOther: '',
  locationId: '',
  maxUses: '',
  scopes: [],
  maxSaleAmount: '',
  maxSalesPerHour: '',
  dailyTotalCap: '',
  allowedFrom: '',
  allowedTo: '',
  allowedCidrs: '',
  bindDevice: false,
  maxDiscountPercent: '',
  maxDiscountAmount: '',
});

/** 'HH:MM' from a time input to minutes after midnight (or undefined when empty). */
export const toMinutes = (v: string): number | undefined =>
  /^\d{2}:\d{2}$/.test(v) ? Number(v.slice(0, 2)) * 60 + Number(v.slice(3)) : undefined;

export const fromMinutes = (m: number | null | undefined): string =>
  m == null ? '' : `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

/** Same pattern as the server DTO (CIDR_RE) so a bad network is caught before the request. */
export const CIDR_RE = /^(?:(?:25[0-5]|2[0-4]\d|1?\d?\d)(?:\.(?:25[0-5]|2[0-4]\d|1?\d?\d)){3}(?:\/(?:3[0-2]|[12]?\d))?|[0-9a-fA-F:]{2,39}(?:\/(?:12[0-8]|1[01]\d|\d{1,2}))?)$/;

export const hasDiscount = (f: Pick<LinkForm, 'scopes'>) => f.scopes.includes(DISCOUNT_SCOPE);

/** Hours the link will live, or null when the "other" number is missing or out of range. */
export function expiryHours(f: Pick<LinkForm, 'expiry' | 'expiryOther'>): number | null {
  const raw = f.expiry === 'other' ? f.expiryOther.trim() : f.expiry;
  if (!/^\d+$/.test(raw)) return null;
  const n = Number(raw);
  return n >= 1 && n <= MAX_EXPIRY_HOURS ? n : null;
}

const isMoney = (v: string) => /^\d+(\.\d{1,2})?$/.test(v.trim()) && Number(v) > 0;
const isWhole = (v: string) => /^\d+$/.test(v.trim()) && Number(v) >= 1;

/** Activities with their dependencies: sell is needed for discounts, view for table orders. */
export function toggleScope(scopes: string[], key: string): string[] {
  const on = scopes.includes(key);
  let next = on ? scopes.filter((s) => s !== key) : [...scopes, key];
  // POS-I267: taking table orders needs the view activity (the Tables tab); removing the view removes the write too.
  if (!on && key === 'restaurant:write' && !next.includes('restaurant:read')) next = [...next, 'restaurant:read'];
  if (on && key === 'restaurant:read') next = next.filter((s) => s !== 'restaurant:write');
  // POS-I275: a discount is taken on a sale, so without the sell activity there is no discount.
  if (on && key === SELL_SCOPE) next = next.filter((s) => s !== DISCOUNT_SCOPE);
  return next;
}

export function validate(f: LinkForm, ctx: ValidateCtx = {}): FormProblem[] {
  const out: FormProblem[] = [];
  const add = (field: string, section: SectionKey, key: string, params?: FormProblem['params']) => out.push({ field, section, key, params });

  if (f.name.trim().length < 2) add('link-name', 'basics', 'problemName');
  if (expiryHours(f) === null) add(f.expiry === 'other' ? 'link-expiry-other' : 'link-expiry', 'basics', 'problemExpiry');
  if (f.maxUses.trim() && !(isWhole(f.maxUses) && Number(f.maxUses) <= 1000)) add('link-max', 'basics', 'problemMaxUses');

  const activities = f.scopes.filter((s) => s !== DISCOUNT_SCOPE);
  if (activities.length === 0) add('section-activities', 'activities', 'problemActivity');

  if (hasDiscount(f)) {
    if (!f.scopes.includes(SELL_SCOPE)) add('disc-switch', 'discounts', 'discNeedsSell');
    const pctRaw = f.maxDiscountPercent.trim();
    const pct = Number(pctRaw);
    if (!pctRaw) add('disc-percent', 'discounts', 'problemPercent');
    else if (!/^\d+(\.\d{1,2})?$/.test(pctRaw) || !(pct >= 0.01 && pct <= 100)) add('disc-percent', 'discounts', 'problemPercent');
    else if (ctx.pinThreshold != null && pct >= ctx.pinThreshold) add('disc-percent', 'discounts', 'discPinLimit', { pct: ctx.pinThreshold });
    if (f.maxDiscountAmount.trim() && !isMoney(f.maxDiscountAmount)) add('disc-amount', 'discounts', 'problemPositive');
  }

  if (f.maxSaleAmount.trim() && !isMoney(f.maxSaleAmount)) add('cap-amount', 'limits', 'problemPositive');
  if (f.maxSalesPerHour.trim() && !isWhole(f.maxSalesPerHour)) add('cap-hour', 'limits', 'problemWhole');
  if (f.dailyTotalCap.trim() && !isMoney(f.dailyTotalCap)) add('cap-day', 'limits', 'problemPositive');
  if (!!f.allowedFrom !== !!f.allowedTo) add(f.allowedFrom ? 'cap-to' : 'cap-from', 'limits', 'problemHoursPair');
  const cidrs = f.allowedCidrs.split(',').map((c) => c.trim()).filter(Boolean);
  const badNet = cidrs.find((c) => !CIDR_RE.test(c));
  if (badNet) add('cap-cidr', 'limits', 'problemNetwork', { value: badNet });
  else if (cidrs.length > 20) add('cap-cidr', 'limits', 'problemNetworkCount');
  return out;
}

/** Request body. Fields of a link without discounts are exactly what the page sent before POS-I275. */
export function buildPayload(f: LinkForm): Record<string, unknown> {
  const from = toMinutes(f.allowedFrom);
  const to = toMinutes(f.allowedTo);
  return {
    name: f.name.trim(),
    scopes: f.scopes,
    expiresInHours: expiryHours(f) ?? Number(DEFAULT_EXPIRY),
    ...(f.locationId ? { locationId: f.locationId } : {}),
    ...(f.maxUses ? { maxUses: Number(f.maxUses) } : {}),
    ...(f.maxSaleAmount ? { maxSaleAmount: Number(f.maxSaleAmount) } : {}),
    ...(f.maxSalesPerHour ? { maxSalesPerHour: Number(f.maxSalesPerHour) } : {}),
    ...(f.dailyTotalCap ? { dailyTotalCap: Number(f.dailyTotalCap) } : {}),
    ...(from !== undefined && to !== undefined ? { allowedFromMinute: from, allowedToMinute: to } : {}),
    ...(f.allowedCidrs.trim() ? { allowedCidrs: f.allowedCidrs.split(',').map((c) => c.trim()).filter(Boolean) } : {}),
    ...(f.bindDevice ? { bindDevice: true } : {}),
    ...(hasDiscount(f)
      ? {
          maxDiscountPercent: Number(f.maxDiscountPercent),
          ...(f.maxDiscountAmount.trim() ? { maxDiscountAmount: Number(f.maxDiscountAmount) } : {}),
        }
      : {}),
  };
}

export type SectionState = 'complete' | 'problem' | 'notStarted';

/** Text state of a section in the rail. `attempted` = the user already pressed Create (problems are then shown). */
export function sectionState(section: SectionKey, f: LinkForm, problems: FormProblem[], attempted: boolean): SectionState {
  const mine = problems.filter((p) => p.section === section);
  if (section === 'review') {
    if (problems.length > 0) return attempted ? 'problem' : 'notStarted';
    return f.name.trim() && f.scopes.length ? 'complete' : 'notStarted';
  }
  if (mine.length > 0) {
    if (attempted) return 'problem';
    // Before the first attempt only a section the user has started can show a problem.
    if (section === 'limits') return startedLimits(f) ? 'problem' : 'notStarted';
    if (section === 'discounts') return hasDiscount(f) ? 'problem' : 'notStarted';
    return 'notStarted';
  }
  switch (section) {
    case 'basics':
      return f.name.trim() ? 'complete' : 'notStarted';
    case 'activities':
      return f.scopes.some((s) => s !== DISCOUNT_SCOPE) ? 'complete' : 'notStarted';
    case 'discounts':
      return hasDiscount(f) ? 'complete' : 'notStarted';
    default:
      return startedLimits(f) ? 'complete' : 'notStarted';
  }
}

const startedLimits = (f: LinkForm) =>
  !!(f.maxSaleAmount || f.maxSalesPerHour || f.dailyTotalCap || f.allowedFrom || f.allowedTo || f.allowedCidrs.trim() || f.bindDevice);

// ---------------------------------------------------------------- summary

export interface SummaryLine {
  /** i18n key (namespace mobileLinks). */
  key: string;
  params?: Record<string, string | number>;
}
export interface LinkSummaryData {
  can: SummaryLine[];
  cannot: SummaryLine[];
  limits: SummaryLine[];
}
export interface SummaryFormatters {
  money: (n: number) => string;
  date: (d: Date) => string;
  /** Short relative length of time, e.g. "24 h". */
  rel: (ms: number) => string;
}
export interface SummaryCtx {
  fmt: SummaryFormatters;
  expiresAt: Date | null;
  locationName?: string | null;
  now?: Date;
}

const scopeLineKey = (s: string) => `canScope_${s.replace(/[:-]/g, '_')}`;
const BASE_ACTIVITIES = ['pos:create', 'pos:read', 'customers:write'];

export function buildSummary(f: LinkForm, catalog: Pick<CatalogEntry, 'key'>[], ctx: SummaryCtx): LinkSummaryData {
  const known = new Set(catalog.map((c) => c.key));
  const can: SummaryLine[] = [];
  const cannot: SummaryLine[] = [];
  const limits: SummaryLine[] = [];

  const order = [...BASE_ACTIVITIES, 'restaurant:read', 'restaurant:write'];
  for (const s of order) {
    if (f.scopes.includes(s)) can.push({ key: scopeLineKey(s) });
  }
  if (hasDiscount(f)) {
    const pct = Number(f.maxDiscountPercent);
    if (f.maxDiscountPercent.trim() && pct > 0 && pct <= 100) {
      const amount = Number(f.maxDiscountAmount);
      can.push(
        f.maxDiscountAmount.trim() && amount > 0
          ? { key: 'canDiscountAmount', params: { pct, amount: ctx.fmt.money(amount) } }
          : { key: 'canDiscount', params: { pct } },
      );
    }
  } else {
    cannot.push({ key: 'cannotDiscount' });
  }
  for (const s of BASE_ACTIVITIES) if (!f.scopes.includes(s)) cannot.push({ key: scopeLineKey(s) });
  if (known.has('restaurant:write') && !f.scopes.includes('restaurant:write')) cannot.push({ key: 'cannotTables' });
  cannot.push({ key: 'cannotVoid' }, { key: 'cannotMoney' }, { key: 'cannotAdmin' });

  if (ctx.expiresAt) {
    const ms = ctx.expiresAt.getTime() - (ctx.now ?? new Date()).getTime();
    limits.push({ key: 'limExpires', params: { date: ctx.fmt.date(ctx.expiresAt), rel: ctx.fmt.rel(Math.max(0, ms)) } });
  }
  const maxUses = Number(f.maxUses);
  limits.push(f.maxUses.trim() && maxUses > 0 ? { key: 'limOpens', params: { n: maxUses } } : { key: 'limOpensUnlimited' });
  if (isMoney(f.maxSaleAmount)) limits.push({ key: 'limInvoice', params: { amount: ctx.fmt.money(Number(f.maxSaleAmount)) } });
  if (isWhole(f.maxSalesPerHour)) limits.push({ key: 'limPerHour', params: { n: Number(f.maxSalesPerHour) } });
  if (isMoney(f.dailyTotalCap)) limits.push({ key: 'limDaily', params: { amount: ctx.fmt.money(Number(f.dailyTotalCap)) } });
  if (f.allowedFrom && f.allowedTo) limits.push({ key: 'limHours', params: { from: f.allowedFrom, to: f.allowedTo } });
  const nets = f.allowedCidrs.split(',').map((c) => c.trim()).filter(Boolean).length;
  if (nets > 0) limits.push({ key: 'limNetworks', params: { n: nets } });
  if (f.bindDevice) limits.push({ key: 'limBound' });
  if (ctx.locationName) limits.push({ key: 'limLocation', params: { name: ctx.locationName } });
  limits.push({ key: 'limOnePhone' });
  return { can, cannot, limits };
}

// ---------------------------------------------------------------- stored link -> form (details drawer, create similar)

export interface LinkRowLike {
  name: string;
  scopes: string[];
  locationId?: string | null;
  maxUses?: number | null;
  maxSaleAmount?: number | null;
  maxSalesPerHour?: number | null;
  dailyTotalCap?: number | null;
  allowedFromMinute?: number | null;
  allowedToMinute?: number | null;
  allowedCidrs?: string[] | null;
  bindDevice?: boolean;
  maxDiscountPercent?: number | null;
  maxDiscountAmount?: number | null;
}

const str = (n: number | null | undefined) => (n == null ? '' : String(n));

/** The stored settings of a link as form values (expiry is the default; callers show the stored date themselves). */
export function formFromRow(r: LinkRowLike): LinkForm {
  return {
    name: r.name,
    expiry: DEFAULT_EXPIRY,
    expiryOther: '',
    locationId: r.locationId ?? '',
    maxUses: str(r.maxUses),
    scopes: [...r.scopes],
    maxSaleAmount: str(r.maxSaleAmount),
    maxSalesPerHour: str(r.maxSalesPerHour),
    dailyTotalCap: str(r.dailyTotalCap),
    allowedFrom: r.allowedFromMinute != null && r.allowedToMinute != null ? fromMinutes(r.allowedFromMinute) : '',
    allowedTo: r.allowedFromMinute != null && r.allowedToMinute != null ? fromMinutes(r.allowedToMinute) : '',
    allowedCidrs: (r.allowedCidrs ?? []).join(', '),
    bindDevice: !!r.bindDevice,
    maxDiscountPercent: str(r.maxDiscountPercent),
    maxDiscountAmount: str(r.maxDiscountAmount),
  };
}

/**
 * Create similar (U3): copies only what the CURRENT user may still give. Scopes the user no longer holds are
 * dropped (and returned in `dropped`), the discount goes with the sell activity, the name gets the copy suffix
 * (80 characters at most), the expiry is the default, never an old date, and there is no token.
 */
export function similarForm(r: LinkRowLike, catalog: CatalogEntry[], copySuffix: string): { form: LinkForm; dropped: string[] } {
  const grantable = new Set(catalog.filter((c) => c.grantable).map((c) => c.key));
  const base = formFromRow(r);
  let scopes = base.scopes.filter((s) => grantable.has(s));
  if (!scopes.includes(SELL_SCOPE)) scopes = scopes.filter((s) => s !== DISCOUNT_SCOPE);
  if (!scopes.includes('restaurant:read')) scopes = scopes.filter((s) => s !== 'restaurant:write');
  const dropped = base.scopes.filter((s) => !scopes.includes(s));
  const suffix = ` ${copySuffix}`;
  const name = r.name.length + suffix.length > 80 ? `${r.name.slice(0, 80 - suffix.length).trimEnd()}${suffix}` : `${r.name}${suffix}`;
  const keepDiscount = scopes.includes(DISCOUNT_SCOPE);
  return {
    form: {
      ...base,
      name,
      scopes,
      maxDiscountPercent: keepDiscount ? base.maxDiscountPercent : '',
      maxDiscountAmount: keepDiscount ? base.maxDiscountAmount : '',
    },
    dropped,
  };
}

// ---------------------------------------------------------------- server errors

export interface ServerErrorBody {
  status?: number;
  code?: string;
  message?: string | string[];
  threshold?: number;
  used?: number;
  max?: number;
}
export interface MappedServerError {
  key: string;
  params?: Record<string, string | number>;
  /** Set when the server told us the shop's manager-PIN threshold. */
  pinThreshold?: number;
  /** The field the message belongs to (focus target), when there is one. */
  field?: string;
}

/** Translated-key mapping of the create refusals: no raw API text is ever shown. */
export function mapServerError(e: ServerErrorBody): MappedServerError {
  switch (e.code) {
    case 'LINK_DISCOUNT_PIN_LIMIT':
      return { key: 'discPinLimit', params: { pct: e.threshold ?? 0 }, pinThreshold: e.threshold, field: 'disc-percent' };
    case 'LINK_DISCOUNT_NEEDS_SELL':
      return { key: 'discNeedsSell', field: 'disc-switch' };
    case 'LINK_DISCOUNT_PERCENT_REQUIRED':
      return { key: 'problemPercent', field: 'disc-percent' };
    case 'LINK_DISCOUNT_CAPS_WITHOUT_SCOPE':
      return { key: 'errCapsWithoutScope', field: 'disc-switch' };
    case 'LINKS_LOCKED':
      return { key: 'lockedBanner' };
    case 'MOBILE_LINK_LIMIT':
      return { key: 'limitReached', params: { used: e.used ?? 0, max: e.max ?? 0 } };
    default:
      break;
  }
  if (e.status === 403) return { key: 'errCeiling' };
  return { key: 'errGeneric' };
}
